import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { countries } from './countries/countries'
import Spinner from '../spinner/spinner';
import Card from '../Card';

const ActivationRequest = () => {
    const { lang } = useParams();
    const scrollRef = useRef(null);
    const [userData, setUserData] = useState({
        projectNumber: '',
        companyName: '',
        street: '',
        zip: '',
        city: '',
        country: lang === 'de' ? 'Deutschland' : 'Germany',
        billingEmail: '',
        contactEmail: '',
        comments: ''
    });
    const [errorSubmit, setErrorSubmit] = useState(false);
    const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [cards, setCards] = useState([
        { tagId: '', reference: '', validFrom: '', validTo: '' }, // Initiale Werte für die erste Zeile
    ]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        // Kombiniere `userData` und `cards` in ein Objekt
        const payload = {
            ...userData,
            cards: cards,
        };

        // const apiEndpoint = lang === 'de' ? '/api/v1/order-de' : '/api/v1/order-en';
        // const apiEndpoint = '/api/v1/order';

        try {
            const apiEndpoint = '/api/v1/activation-request'; // Beispiel-Endpunkt
            const response = await fetch(apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': process.env.REACT_APP_SPIRII_CHARGING_CARD_API_KEY
                },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                setShowSubmitSuccess(true);
            } else {
                const errorData = await response.json();
                console.error('Fehler bei der Anfrage:', errorData);
                setErrorSubmit(true);
            }
        } catch (error) {
            console.error('Netzwerkfehler:', error);
            setErrorSubmit(true);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="App container-fluid bg-appBackground py-12 py-auto h-full min-h-screen text-center">
                <Spinner />
            </div>
        )
    }

    // Hinzufügen eines neuen Kartenfelds
    const addCardField = () => {
        setCards([...cards, { tagId: '', reference: '', validFrom: '', validTo: '' }]);

        // Verzögertes Scrollen, damit der neue Inhalt zuerst gerendert wird
        setTimeout(() => {
            scrollRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end'
            });
        }, 100);
    };

    // Entfernen eines Kartenfelds
    const removeCardField = (index) => {
        const updatedCards = [...cards];
        updatedCards.splice(index, 1);
        setCards(updatedCards);
    };

    // Aktualisieren der Werte in einer Zeile
    const handleCardChange = (index, field, value) => {
        const updatedCards = [...cards];
        updatedCards[index][field] = value;
        setCards(updatedCards);
    };

    return (
        <div className="flex flex-col">
            {errorSubmit && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-appBackground bg-opacity-80 backdrop-blur-md p-6 rounded-lg shadow-xl w-11/12 max-w-md relative">
                        {/* Schließen-Button */}
                        <button
                            onClick={() => setErrorSubmit(false)}
                            className="absolute top-3 right-3 text-white text-2xl hover:text-gray-300 transition-colors font-avenir"
                            aria-label={lang === "de" ? "Schließen" : "Close"}
                        >
                            &times;
                        </button>

                        {/* Titel */}
                        <h2 className="text-xl font-bold text-red-500 mb-4 text-center font-avenir">
                            {lang === "de" ? "Fehler bei der Konfigurationsanfrage!" : "Error with your configuration request!"}
                        </h2>

                        {/* Beschreibung */}
                        <p className="text-sm text-white leading-relaxed text-center font-avenir">
                            {lang === "de"
                                ? "Oops, es ist ein Fehler bei der Konfigurationsanfrage aufgetreten. Bitte senden Sie eine E-Mail an service.connect@gp-joule.de. Unser Team hilft Ihnen schnellstmöglich weiter."
                                : "Oops, an error has occurred with your configuration request. Please send an email to service.connect@gp-joule.de. Our team will assist you as soon as possible."}
                        </p>

                        {/* Schließen-Button */}
                        <button
                            onClick={() => setErrorSubmit(false)}
                            className="mt-6 w-full text-white py-2 hover:bg-white hover:text-black hover:shadow-secondary-outline custom-transition font-avenir border-white border-2 "
                        >
                            {lang === "de" ? "Fehlermeldung schließen" : "Close error message"}
                        </button>
                    </div>
                </div>
            )}
            <div className="">
                {!showSubmitSuccess ? (
                    <form onSubmit={(event) => handleSubmit(event)}>
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <label className="block text-sm  text-white font-avenir">
                                    {lang === 'de' ? 'Details für die Abrechnung' : 'Charging Cards'}
                                </label>
                                <div >
                                    <input
                                        type="text"
                                        id="projectNumber"
                                        name="projectNumber"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm block p-2 w-full mx-auto font-avenir text-md hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                        placeholder={lang === "de" ? "Projektnummer *" : "Project Number *"}
                                        required
                                        onChange={(event) => setUserData({ ...userData, projectNumber: event.target.value })} />
                                </div>
                                <div >
                                    <input
                                        type="text"
                                        id="companyName"
                                        name="companyName"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm block p-2 w-full mx-auto font-avenir text-md hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                        // placeholder="Company Name *"
                                        placeholder={lang === "de" ? "Firmenname *" : "Company Name *"}
                                        required
                                        onChange={(event) => setUserData({ ...userData, companyName: event.target.value })} />
                                </div>
                                <div >
                                    <input
                                        type="text"
                                        id="streetAndNumber"
                                        name="streetAndNumber"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm block p-2 w-full mx-auto font-avenir text-md hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                        placeholder={lang === "de" ? "Straße und Nr. *" : "Street, No. *"}
                                        required
                                        onChange={(event) => setUserData({ ...userData, street: event.target.value })}
                                    />
                                </div>
                                <div >
                                    <input
                                        type="text"
                                        id="zip"
                                        name="zip"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm block p-2 w-full mx-auto font-avenir text-md hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                        placeholder={lang === "de" ? "PLZ *" : "Postal / Zip Code *"}
                                        required
                                        onChange={(event) => setUserData({ ...userData, zip: event.target.value })}
                                    />
                                </div>
                                <div >
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm block p-2 w-full mx-auto font-avenir text-md hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                        placeholder={lang === "de" ? "Ort *" : "City *"}
                                        required
                                        onChange={(event) => setUserData({ ...userData, city: event.target.value })}
                                    />
                                </div>
                                <div >
                                    <select
                                        id="countries"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm block p-2 w-full mx-auto font-avenir text-md hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                        onChange={(event) =>
                                            setUserData({ ...userData, country: event.target.value })
                                        }
                                    >
                                        {Object.entries(countries).map(([code, names]) => (
                                            <option key={code} value={names[lang]}>
                                                {names[lang]} {/* Zeigt die Sprache basierend auf `lang` */}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div >
                                    <input
                                        type="email"
                                        id="billingEmail"
                                        name="billingEmail"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm block p-2 w-full mx-auto font-avenir text-md hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                        placeholder={lang === "de" ? "Rechnungsemailadresse *" : "Billing Email Address *"}
                                        required
                                        onChange={(event) => setUserData({ ...userData, billingEmail: event.target.value })} />
                                </div>
                            </div>
                            {/* Dynamische Kartenfelder */}
                            <div className="space-y-3">
                                <label className="block text-sm  text-white font-avenir">
                                    {lang === 'de' ? 'Ladekarten' : 'Charging Cards'}
                                </label>
                                {cards.map((card, index) => (
                                    <Card
                                        key={index}
                                        index={index}
                                        card={card}
                                        handleCardChange={handleCardChange}
                                        removeCardField={cards.length > 1 ? removeCardField : null}
                                        lang={lang}
                                    />
                                ))}
                                <button
                                    type="button"
                                    onClick={addCardField}
                                    className="w-full text-white py-2 hover:bg-white hover:text-black hover:shadow-secondary-outline custom-transition font-avenir border-white border-2"
                                >
                                    {lang === 'de'
                                        ? '+ Weitere Karte hinzufügen'
                                        : '+ Add Another Card'}
                                </button>
                            </div>
                            <div className="space-y-3">
                                <label className="block text-white text-sm font-avenir">
                                    {lang === 'de' ? 'Weitere Anmerkungen' : 'Additional Comments'}
                                </label>
                                <textarea
                                    className="block w-full text-sm border border-gray-300 p-2 text-gray-900 font-avenir hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                    placeholder={lang === 'de' ? 'Hier können Sie zusätzliche Anmerkungen eingeben...' : 'You can add additional comments here...'}
                                    rows="4"
                                    onChange={(event) => setUserData({ ...userData, comments: event.target.value })}
                                />
                                <input
                                    type="email"
                                    id="contactEmailAddress"
                                    name="contactEmailAddress"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm block p-2 w-full mx-auto font-avenir text-md hover:border-primary focus:border-primary focus:ring-transparent custom-transition"
                                    placeholder={lang === "de" ? "E-Mail für Rückfragen *" : "Email for Inquiries  *"}
                                    required
                                    onChange={(event) => setUserData({ ...userData, contactEmail: event.target.value })} />
                            </div>
                            <button
                                type="submit"
                                className="w-full bg-primary text-black py-2 font-avenir custom-transition hover:shadow-primary-outline"
                            >
                                {lang === 'de' ? 'Absenden' : 'Submit'}
                            </button>
                            <div ref={scrollRef}></div>
                        </div>
                    </form>
                ) : (
                    <div>
                        <h3 className="text-primary text-left font-avenir font-extrabold text-xl mt-10">
                            {lang === 'de'
                                ? 'Vielen Dank für Ihre Konfigurationsanfrage.'
                                : 'Thank you for your configuration request.'}
                        </h3>
                        <p className="text-white text-left font-avenir">
                            {lang === 'de'
                                ? 'Ihre Konfigurationsanfrage wurde erfolgreich übermittelt. Unser Team wird die Konfiguration und Aktivierung Ihrer Ladekarten zeitnah bearbeiten. Nach erfolgreicher Aktivierung erhalten Sie eine Bestätigung. Sollten wir Rückfragen haben, werden wir uns mit Ihnen in Verbindung setzen.'
                                : 'Your configuration request has been successfully submitted. Our team will process the configuration and activation of your charging cards promptly. You will receive a confirmation upon successful activation. If we have any questions, we will contact you.'}
                        </p>
                    </div>
                )}
            </div>
        </div >
    )
}

export default ActivationRequest;